import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import BigModal from '../../../components/Modals/BigModal'
import AnDateField from '../../../components/DateTime/DateField'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import DocumentsField from '../../../helpers/DocumentsField'
import {
  DEMO_UPLOAD_MESSAGE,
  uploadDocument,
} from '../../../helpers/uploadDocument'

// A staff member uploads their own certificate for one course. The record
// always lands in Personnel's approval queue (the server forces it), so the
// certificate is required here - without it there is nothing to approve.
const Upload = ({ open, onClose, selectedCourse, successAction }) => {
  const { user } = useContext(UserContext)
  const [date, setDate] = useState(null)
  const [notes, setNotes] = useState('')
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  // A fresh form each time it opens
  useEffect(() => {
    if (!open) return
    setDate(null)
    setNotes('')
    setFiles([])
    setError(null)
    setUploading(false)
  }, [open])

  const canSubmit =
    !!date &&
    date.isValid?.() &&
    !date.isAfter(moment(), 'day') &&
    files.length === 1 &&
    !uploading

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return
    setUploading(true)
    setError(null)
    try {
      let certificate
      try {
        certificate = await uploadDocument(
          files[0],
          'employees/mandatorytraining',
        )
      } catch (err) {
        throw new Error(
          err.message === DEMO_UPLOAD_MESSAGE
            ? err.message
            : 'Unable to upload your certificate. Check your connection and try again.',
        )
      }

      const res = await API.post('/employees/addTrainingRecord', {
        employee: user.employee._id,
        course: selectedCourse._id,
        date: date.toISOString(),
        note: notes,
        certificate,
        requiresApproval: true,
      })
      if (res.status !== 200) {
        throw new Error(
          res.data?.error ||
            'Unable to save your certificate. Please try again later.',
        )
      }
      successAction()
    } catch (err) {
      setError(err.message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <BigModal
      open={open}
      onClose={onClose}
      autoWidth
      style={{ width: { xs: 'calc(100vw - 24px)', sm: 480 }, padding: 3 }}
    >
      <Stack gap={2} component="form" onSubmit={handleSubmit}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
        >
          <Stack>
            <Typography variant="title3" fontWeight={700}>
              Upload a certificate
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedCourse?.title} · sent to Personnel for approval
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <AnDateField
          date={date}
          onChange={(newDate) => setDate(newDate)}
          label="Date Completed"
          required
          disableFuture
          fullWidth
        />

        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional - e.g. training provider or certificate number"
          multiline
          minRows={2}
          fullWidth
        />

        <Stack gap={1}>
          <Typography variant="body2" fontWeight={600}>
            Certificate
          </Typography>
          <DocumentsField
            files={files}
            onFilesChange={setFiles}
            single
            accept="application/pdf,image/*"
            caption="PDF or photo of your certificate, up to 10 MB"
            acceptLabel="PDF or image files"
            maxSizeMB={10}
            pendingLabel="Uploads when you submit"
            disabled={uploading}
          />
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={onClose} color="inherit" disabled={uploading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={!canSubmit}
          >
            {uploading ? 'Uploading…' : 'Submit for approval'}
          </Button>
        </Stack>
      </Stack>
    </BigModal>
  )
}

export default Upload
